import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Server, CreditCard } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPlans = () => {
  const { getAuthHeader } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    max_agents: 2,
    max_servers: 10,
    price_monthly: 19.0,
    price_yearly: 190.0,
    features: "",
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/plans`);
      setPlans(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      max_agents: 2,
      max_servers: 10,
      price_monthly: 19.0,
      price_yearly: 190.0,
      features: "",
      is_active: true,
      sort_order: 0
    });
    setEditingPlan(null);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description,
      max_agents: plan.max_agents,
      max_servers: plan.max_servers,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      features: plan.features.join("\n"),
      is_active: plan.is_active,
      sort_order: plan.sort_order
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      features: form.features.split("\n").filter(f => f.trim())
    };

    try {
      if (editingPlan) {
        await axios.put(`${API_URL}/api/admin/plans/${editingPlan.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Formule mise a jour");
      } else {
        await axios.post(`${API_URL}/api/admin/plans`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Formule creee");
      }
      setDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async () => {
    if (!planToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/admin/plans/${planToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Formule supprimee");
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      fetchPlans();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Formules</h1>
          <p className="text-slate-400 mt-1">Gerez les offres d&apos;abonnement</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700">
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle formule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingPlan ? "Modifier la formule" : "Nouvelle formule"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nom</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ordre d&apos;affichage</Label>
                  <Input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Max agents</Label>
                  <Input
                    type="number"
                    value={form.max_agents}
                    onChange={(e) => setForm({ ...form, max_agents: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Max serveurs</Label>
                  <Input
                    type="number"
                    value={form.max_servers}
                    onChange={(e) => setForm({ ...form, max_servers: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Prix mensuel (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price_monthly}
                    onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Prix annuel (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price_yearly}
                    onChange={(e) => setForm({ ...form, price_yearly: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Fonctionnalites (une par ligne)</Label>
                <Textarea
                  value={form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  rows={4}
                  className="bg-slate-950 border-slate-700 text-white"
                  placeholder="Alertes email&#10;Support prioritaire&#10;API complete"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Formule active</Label>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
              </div>

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                {editingPlan ? "Enregistrer" : "Creer la formule"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer la formule</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Etes-vous sur de vouloir supprimer la formule &quot;{planToDelete?.name}&quot; ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div key={plan.id} className={`glass-card rounded-xl p-6 relative ${!plan.is_active ? "opacity-50" : ""}`}>
            {!plan.is_active && (
              <span className="absolute top-2 right-2 text-xs bg-slate-600 text-white px-2 py-1 rounded">
                Inactive
              </span>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <CreditCard className="text-purple-400" size={20} />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white">{plan.name}</h3>
                <p className="text-xs text-slate-400">{plan.description}</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-2xl font-bold text-white">{plan.price_monthly}€<span className="text-sm text-slate-400">/mois</span></p>
              <p className="text-sm text-slate-500">{plan.price_yearly}€/an</p>
            </div>

            <div className="space-y-2 mb-4 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <Server size={14} className="text-indigo-400" />
                <span>{plan.max_servers === 999 ? "Illimite" : plan.max_servers} serveurs</span>
              </div>
              <div className="flex items-center gap-2">
                <Server size={14} className="text-indigo-400" />
                <span>{plan.max_agents === 99 ? "Illimite" : plan.max_agents} types d&apos;agents</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openEdit(plan)}
                className="flex-1 bg-slate-800 hover:bg-slate-700"
              >
                <Pencil size={14} className="mr-1" />
                Modifier
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setPlanToDelete(plan);
                  setDeleteDialogOpen(true);
                }}
                className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPlans;
